import { useEffect, useMemo, useState } from 'react'
import Navbar from '../../components/navbar/navbar'
import OrcamentosService from '../../services/database/OrcamentosService'
import { toast } from 'react-toastify'
import { Form, OverlayTrigger, Table, Tooltip } from 'react-bootstrap'
import ReactPaginate from 'react-paginate'
import Swal from 'sweetalert2'
import moment from 'moment'

import { CustomSpinner } from '../../components/CustomSpinner'
import { CustomButton } from '../../components/CustomButton'
import {
  CaretDown,
  CaretUp,
  PencilFill,
  PlusCircleFill,
  PrinterFill,
  ShareFill,
  Copy,
  ArrowClockwise,
  TrashFill,
} from 'react-bootstrap-icons'
import clientesPDF from '../orcamento/report'
import { formatDate } from '../../utils/formatDate'
import { formatPrice } from '../../utils/formatPrice'
import { useNavigate } from 'react-router-dom'
import { useOrderTable } from '../../hooks/useOrderTable'
import { sendErrorApiMessage } from '../../utils/sendErrorApiMessage'

export function ListarOrcamentos() {
  const [orcamentos, setOrcamentos] = useState([])
  const [isOrcamentosLoading, setIsOrcamentosLoading] = useState(true)

  const [isExportSaleLoading, setIsExportSaleLoading] = useState({})
  const [isCloneRequestLoading, setIsCloneRequestLoading] = useState({})
  const [isDeleteRequestLoading, setIsDeleteRequestLoading] = useState({})
  const [selectedFilterSearch, setSelectedFilterSearch] = useState('cliente')
  const [filterSearch, setFilterSearch] = useState('')
  const [itemOffset, setItemOffset] = useState(0)
  const [itemsPerPage, setItemsPerPage] = useState(10)

  const endOffset = itemOffset + itemsPerPage

  const [pageCount, setPageCount] = useState(0)

  const navigate = useNavigate()

  const {
    sortedArray: sortedOrcamentos,
    check,
    handleOrganize,
    orderType,
  } = useOrderTable(orcamentos, 'pedido')

  const filteredOrcamentos = useMemo(() => {
    const orcamentosFiltered = sortedOrcamentos.filter((orcamento) =>
      String(orcamento[selectedFilterSearch])
        .toLowerCase()
        .includes(String(filterSearch).toLowerCase()),
    )

    if (filterSearch.length > 0 && orcamentosFiltered.length <= 1) {
      setItemOffset(0)
    }

    setPageCount(Math.ceil(orcamentosFiltered.length / itemsPerPage))

    return orcamentosFiltered.slice(itemOffset, endOffset)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    endOffset,
    itemOffset,
    filterSearch,
    sortedOrcamentos,
    orderType.field,
    orderType.type,
  ])

  async function handleExportSale(idOrcamento, index) {
    try {
      setIsExportSaleLoading((prevState) => ({
        ...prevState,
        [index]: true,
      }))

      const dataMessage = await OrcamentosService.exportToSales(idOrcamento)

      await loadOrcamentos()

      toast.success(dataMessage)
    } catch (err) {
      sendErrorApiMessage(err, 'Houve um erro ao exportar a venda!')
    } finally {
      setIsExportSaleLoading((prevState) => ({
        ...prevState,
        [index]: false,
      }))
    }
  }

  async function handleClone(idOrcamento, index) {
    try {
      setIsCloneRequestLoading((prevState) => ({
        ...prevState,
        [index]: true,
      }))

      const responseMessage = await OrcamentosService.clone(idOrcamento)

      toast.success(responseMessage)
      loadOrcamentos()
    } catch (err) {
      sendErrorApiMessage(err, 'Não foi possível clonar o orçamento!')
    } finally {
      setIsCloneRequestLoading((prevState) => ({
        ...prevState,
        [index]: false,
      }))
    }
  }

  async function handleDeleteOrder(idOrcamento, index) {
    try {
      setIsDeleteRequestLoading((prevState) => ({
        ...prevState,
        [index]: true,
      }))

      await OrcamentosService.delete(idOrcamento)

      toast.success('Orçamento deletado com sucesso!')
      loadOrcamentos()
    } catch (err) {
      sendErrorApiMessage(err, 'Não foi possível excluir o orçamento!')
    } finally {
      setIsDeleteRequestLoading((prevState) => ({
        ...prevState,
        [index]: false,
      }))
    }
  }

  async function printPDF(idOrcamento) {
    const { orcamento, itens, empresa, endereco_cliente } =
      await OrcamentosService.getEstimativeToPrint(idOrcamento)

    const {
      pedido,
      cliente,
      data,
      desconto,
      desconto_din,
      id_parceiro,
      cpf,
      cod_vendedor,
      detalhes_pedido,
      valor,
      id_cliente,
    } = orcamento

    const dadosPedido = {
      numeroorc: pedido,
      cliente,
      id_cliente: id_cliente || 0,
      id_pagamento: null,
      cod_vendedor: cod_vendedor || null,
      data: data || moment().format('YYYY-MM-DD'),
      valor,
      desconto: desconto || null,
      desconto_din: desconto_din || 0,
      id_parceiro: id_parceiro || null,
      cpf,
      dadosDoCliente: endereco_cliente,
      detalhes_pedido: detalhes_pedido || 'Não informado',
      itens,
      empresa: [empresa],
    }

    // Função para converter uma imagem para Base64
    async function getBase64ImageFromUrl(url) {
      return new Promise((resolve, reject) => {
        const img = new Image()
        img.setAttribute('crossOrigin', 'anonymous')
        img.onload = function () {
          const canvas = document.createElement('canvas')
          canvas.width = img.width
          canvas.height = img.height
          const ctx = canvas.getContext('2d')
          ctx.drawImage(img, 0, 0)
          const dataURL = canvas.toDataURL('image/jpeg')
          resolve(dataURL)
        }
        img.onerror = function (error) {
          reject(error)
        }
        img.src = url
      })
    }

    // Iterando sobre os itens para converter a imagem em Base64
    for (let i = 0; i < dadosPedido.itens.length; i++) {
      const item = dadosPedido.itens[i]

      try {
        // Verificando se o item tem uma imagem, caso contrário, usar uma imagem padrão
        const imageUrl = item.image
          ? `https://node.ziphub.com.br/imagens/${item.image}`
          : 'https://node.ziphub.com.br/imagens/no-image.jpg'

        // Convertendo a imagem para Base64 e adicionando como uma nova propriedade
        item.base64 = await getBase64ImageFromUrl(imageUrl)
      } catch (error) {
        console.error('Erro ao converter imagem para Base64:', error)

        try {
          // Tentativa de usar a imagem padrão caso ocorra um erro na conversão
          item.base64 = await getBase64ImageFromUrl(
            'https://node.ziphub.com.br/imagens/no-image.jpg',
          )
        } catch (error) {
          console.error('Erro ao converter a imagem padrão para Base64:', error)
          // Pode-se definir uma string base64 padrão aqui ou deixar em branco
          item.base64 = '' // ou qualquer base64 padrão que você queira usar
        }
      }
    }

    clientesPDF(dadosPedido, 'Completo')
  }

  const handlePageClick = (event) => {
    const newOffset = (event.selected * itemsPerPage) % orcamentos.length

    setItemOffset(newOffset)
  }

  async function loadOrcamentos() {
    setIsOrcamentosLoading(true)

    try {
      const data = await OrcamentosService.getAll()

      setOrcamentos(data)
    } catch (error) {
      console.log(`Houve um erro ao listar os orçamentos: ${error}`)
      toast.error('Houve um erro ao listar os orçamentos!')
    } finally {
      setIsOrcamentosLoading(false)
    }
  }

  useEffect(() => {
    loadOrcamentos()
  }, [])

  return (
    <>
      <Navbar />

      <div className="mt-page p-4">
        <h1>Listagem de Orçamentos</h1>

        <div
          style={{
            display: 'flex',
            alignItems: 'flex-end',
            marginBottom: 15,
          }}
        >
          <div>
            <p>Orçamentos por página</p>

            <Form.Select
              onChange={(e) => {
                setItemsPerPage(Number(e.target.value))
              }}
              style={{
                width: '200px',
              }}
            >
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </Form.Select>
          </div>

          <CustomButton
            title="Novo Orçamento"
            color="#e67e22"
            onClick={() => navigate(`/orcamento/${localStorage.idconta}/novo`)}
          >
            <PlusCircleFill />
          </CustomButton>

          <CustomButton
            title="Recarregar"
            color="#27ae60"
            onClick={loadOrcamentos}
          >
            <ArrowClockwise />
          </CustomButton>
        </div>

        <div
          style={{
            display: 'flex',
            gap: 10,
          }}
        >
          <div>
            <p>Filtro de Pesquisa</p>

            <Form.Select
              defaultValue={selectedFilterSearch}
              onChange={(e) => setSelectedFilterSearch(e.target.value)}
              style={{
                width: '200px',
              }}
            >
              <option value="cliente">Cliente</option>
              <option value="pedido">Número</option>
              <option value="parceiro">Parceiro/Arquiteto</option>
            </Form.Select>
          </div>

          <div>
            <p>Pesquisa</p>
            <Form.Control
              value={filterSearch}
              onChange={(e) => setFilterSearch(e.target.value)}
              placeholder="Digite aqui"
              style={{
                width: '400px',
              }}
            />
          </div>
        </div>

        {isOrcamentosLoading ? (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
            }}
          >
            <CustomSpinner size={80} />
          </div>
        ) : (
          <div>
            <Table
              striped
              bordered
              hover
              responsive
            >
              <thead>
                <tr>
                  <th onClick={() => handleOrganize('pedido')}>
                    <span>Número</span>
                    <CaretUp color={check('pedido', 'asc')} />
                    <CaretDown color={check('pedido', 'desc')} />
                  </th>

                  <th onClick={() => handleOrganize('data')}>
                    <span>Data</span>
                    <CaretUp color={check('data', 'asc')} />
                    <CaretDown color={check('data', 'desc')} />
                  </th>

                  <th onClick={() => handleOrganize('cliente')}>
                    <span>Cliente</span>
                    <CaretUp color={check('cliente', 'asc')} />
                    <CaretDown color={check('cliente', 'desc')} />
                  </th>

                  <th onClick={() => handleOrganize('parceiro')}>
                    <span>Parceiro/Arquiteto</span>
                    <CaretUp color={check('parceiro', 'asc')} />
                    <CaretDown color={check('parceiro', 'desc')} />
                  </th>

                  <th onClick={() => handleOrganize('valor')}>
                    <span>Valor</span>
                    <CaretUp color={check('valor', 'asc')} />
                    <CaretDown color={check('valor', 'desc')} />
                  </th>

                  <th>Canal de Venda</th>
                  <th>Pedido Loja</th>
                  <th>Ação</th>
                </tr>
              </thead>

              <tbody>
                {filteredOrcamentos.map((orc, index) => (
                  <tr key={orc.pedido}>
                    <td>{orc.pedido}</td>
                    <td>{formatDate(orc.data)}</td>
                    <td>{orc.cliente}</td>
                    <td>{orc.parceiro}</td>
                    <td>{formatPrice(orc.valor)}</td>
                    <td>{orc.id_loja}</td>
                    <td>{orc.idpedidoloja}</td>
                    <td
                      style={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        justifyContent: 'flex-start',
                      }}
                    >
                      <OverlayTrigger overlay={<Tooltip>Editar</Tooltip>}>
                        <CustomButton
                          color="#f39c12"
                          onClick={() =>
                            navigate(
                              `/orcamento/${localStorage.idconta}/${orc.pedido}`,
                            )
                          }
                        >
                          <PencilFill />
                        </CustomButton>
                      </OverlayTrigger>

                      <OverlayTrigger overlay={<Tooltip>Excluir</Tooltip>}>
                        <CustomButton
                          isLoading={isDeleteRequestLoading[index]}
                          color="#c0392b"
                          onClick={() => {
                            Swal.fire({
                              title: `Excluir orçamento ${orc.pedido}?`,
                              showCancelButton: true,
                              confirmButtonText: 'Sim',
                              confirmButtonColor: '#c0392b',
                              cancelButtonText: `Não`,
                            }).then((result) => {
                              if (result.isConfirmed) {
                                handleDeleteOrder(orc.pedido, index)
                              }
                            })
                          }}
                        >
                          <TrashFill />
                        </CustomButton>
                      </OverlayTrigger>

                      <OverlayTrigger overlay={<Tooltip>Clonar</Tooltip>}>
                        <CustomButton
                          color="#3498db"
                          isLoading={isCloneRequestLoading[index]}
                          onClick={() => {
                            Swal.fire({
                              title: `Clonar orçamento ${orc.pedido}?`,
                              showCancelButton: true,
                              confirmButtonText: 'Sim',
                              confirmButtonColor: '#3498db',
                              cancelButtonText: `Não`,
                              denyButtonColor: '',
                            }).then((result) => {
                              if (result.isConfirmed) {
                                handleClone(orc.pedido, index)
                              }
                            })
                          }}
                        >
                          <Copy />
                        </CustomButton>
                      </OverlayTrigger>

                      <OverlayTrigger overlay={<Tooltip>Imprimir</Tooltip>}>
                        <CustomButton
                          color="#27bac1"
                          onClick={() => printPDF(orc.pedido)}
                        >
                          <PrinterFill />
                        </CustomButton>
                      </OverlayTrigger>

                      <OverlayTrigger
                        overlay={<Tooltip>Transformar em Pedido</Tooltip>}
                      >
                        <CustomButton
                          color="#7445e1"
                          onClick={() => handleExportSale(orc.pedido, index)}
                          isLoading={isExportSaleLoading[index]}
                        >
                          <ShareFill />
                        </CustomButton>
                      </OverlayTrigger>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>

            <ReactPaginate
              breakLabel="..."
              nextLabel="Próxima"
              onPageChange={handlePageClick}
              pageRangeDisplayed={5}
              pageCount={pageCount}
              previousLabel="Anterior"
              renderOnZeroPageCount={null}
              containerClassName="pagination"
              activeClassName="active"
            />
          </div>
        )}
      </div>
    </>
  )
}
